import {
  ExcludeDescendants,
  KnowledgeGraphManagerApi,
  NodeResult,
  NodesResolveRequest,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  AdaptedEntity,
  Column,
  GenericEntityType,
  GenericProviderType,
  KindEnum,
  Pagination,
  Provider,
  ProviderFilters,
  SearchFilter,
  TemplateColumn,
  TemplateEntity,
  TextFieldValue,
  WbBadge,
  WbComponentsListArray,
  WbResponsiveTableToolbar,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { Box, Tooltip, Typography } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useApi } from '@mainstage/core-plugin-api';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 10;

/** Default number of children fetched per page when expanding a tree node. */
const DEFAULT_CHILDREN_PAGE_SIZE = 20;

/**
 * Alternative traversal + filtering used when expanding a node whose
 * `rdf:type` equals `nodeType` (e.g. a `skos:ConceptScheme`), instead of
 * the picker's default `traverse`. Lets a "container" node type use a
 * different single-hop traversal than the rest of the tree — e.g. to reach
 * true top-level members via `excludeDescendants` when the container →
 * member relation alone would also surface deeper descendants.
 */
export interface SchemeExpansionConfig {
  /** `rdf:type` value that triggers this expansion mode. */
  nodeType: string;
  traverse?: {
    relations?: string[];
    inverseRelations?: string[];
  };
  excludeDescendants?: ExcludeDescendants;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolves the display label for a search-tree result.
 * Uses the first scalar value of `labelProperty` when set; falls back to the
 * node URI otherwise.
 */
function resolveLabel(
  id: string,
  properties: Record<string, string | string[]>,
  labelProperty: string | undefined,
): string {
  if (!labelProperty) return id;
  const val = properties[labelProperty];
  if (Array.isArray(val)) return val[0] ?? id;
  return typeof val === 'string' ? val : id;
}

/**
 * Converts a NodeResult to the GenericEntityType shape expected by
 * WbGeneralPurposeEntityPicker. Stores the resolved label in `__metadata.name`
 * so that the EntitySearchPicker can restore the chip label when the user
 * navigates back to a step using `returnField: full`.
 */
function toGenericEntityType(
  result: NodeResult,
  labelProperty: string | undefined,
): GenericEntityType {
  const label = resolveLabel(result.id, result.properties ?? {}, labelProperty);
  return {
    __metadata: { name: label, kind: KindEnum.businessConcept },
    id: result.id,
    type: result.type,
    properties: result.properties,
    childrenCount: result.childrenCount,
  };
}

/**
 * Merges `schemeExpansion`'s relations into `traverse` so a full-text search
 * (or ancestor-path resolution) starting from a `schemeExpansion` container
 * node (e.g. a skos:ConceptScheme) can actually reach its members when the
 * container → member relation isn't part of the default traverse (e.g.
 * skos:inScheme vs skos:narrower/skos:broader). Without this, the backend
 * BFS never leaves the start nodes, so `searchNodes` returns zero matches
 * for any concept only reachable via the scheme-expansion relation — which
 * in turn makes `fetchAncestors` fall back to a bare, ancestor-less path.
 *
 * Returns the widened `traverse` to use for search-candidate BFS, plus an
 * `ancestorTraverse` that prefers the original (narrower) relations for
 * chain reconstruction — so a node with both a real parent and a container
 * link doesn't get a non-deterministic/incorrect parent.
 */
function buildSchemeAwareSearchTraverse(
  traverse: NodesResolveRequest['traverse'],
  schemeExpansion: SchemeExpansionConfig | undefined,
): {
  searchTraverse: NodesResolveRequest['traverse'];
  ancestorTraverse: NodesResolveRequest['traverse'] | undefined;
} {
  if (!schemeExpansion) {
    return { searchTraverse: traverse, ancestorTraverse: undefined };
  }
  return {
    searchTraverse: {
      ...traverse,
      relations: Array.from(
        new Set([
          ...(traverse?.relations ?? []),
          ...(schemeExpansion.traverse?.relations ?? []),
        ]),
      ),
      inverseRelations: Array.from(
        new Set([
          ...(traverse?.inverseRelations ?? []),
          ...(schemeExpansion.traverse?.inverseRelations ?? []),
        ]),
      ),
    },
    ancestorTraverse: traverse,
  };
}

/**
 * Converts a column `path` shorthand to a nunjucks expression:
 *   - Already-nunjucks (contains `{{`): returned as-is
 *   - Top-level fields `id` / `type`: `{{id}}` / `{{type}}`
 *   - Predicate name (e.g. `skos:prefLabel`): `{{properties['skos:prefLabel']}}`
 */
function normalizeColumnPath(path: string): string {
  if (path.includes('{{')) return path;
  if (path === 'id' || path === 'type') return `{{${path}}}`;
  return `{{properties['${path}']}}`;
}

/**
 * Extracts the raw property value for the given normalized nunjucks path.
 */
function resolveRawValue(
  normalizedPath: string,
  row: GenericEntityType,
): unknown {
  const propMatch = normalizedPath.match(/^\{\{properties\['(.+)'\]\}\}$/);
  if (propMatch) {
    return (row.properties as Record<string, unknown>)?.[propMatch[1]];
  }
  const topMatch = normalizedPath.match(/^\{\{(\w+)\}\}$/);
  if (topMatch) {
    return (row as Record<string, unknown>)[topMatch[1]];
  }
  return undefined;
}

/**
 * Builds a renderCell function for a column.
 * If the raw value is an array, each item is rendered as a chip; otherwise
 * falls back to a plain text node.
 */
function buildRenderCell(normalizedPath: string): Column['renderCell'] {
  return (row: GenericEntityType) => {
    const raw = resolveRawValue(normalizedPath, row);
    if (Array.isArray(raw) && raw.length > 0) {
      const chips = (raw as unknown[]).map((item, idx) => (
        <WbBadge key={idx} variant="stroke" color="primary">
          {String(item)}
        </WbBadge>
      ));
      return <WbComponentsListArray gap={8}>{chips}</WbComponentsListArray>;
    }
    const text = raw !== undefined && raw !== null ? String(raw) : '-';
    return (
      <Tooltip title={text} placement="bottom-start" enterDelay={600}>
        <Typography
          variant="body2"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    );
  };
}

/**
 * Normalises TemplateColumn definitions into full Column objects with renderCell
 * attached. Falls back to a single "URI" column when `columnDefs` is not provided.
 *
 * The first column gets an enhanced renderCell that, in search mode, shows the
 * ancestor breadcrumb path below the label when the entity carries `__treePath`.
 */
function buildColumns(columnDefs: TemplateColumn[] | undefined): Column[] {
  const raw: Column[] =
    columnDefs && columnDefs.length > 0
      ? columnDefs.map(c => {
          const normalizedPath =
            'path' in c ? normalizeColumnPath(c.path) : `{{${c.value}}}`;
          return {
            name: c.name,
            path: normalizedPath,
            renderCell: buildRenderCell(normalizedPath),
          };
        })
      : [
          {
            name: 'URI',
            path: '{{id}}',
            renderCell: buildRenderCell('{{id}}'),
          },
        ];

  // Wrap the first column to show the ancestor breadcrumb when available.
  if (raw.length > 0) {
    const first = raw[0];
    const baseRender = first.renderCell!;
    first.renderCell = (row: GenericEntityType) => {
      const base = baseRender(row);
      const treePath = row.__treePath as string[] | undefined;
      // Only show the breadcrumb path in search mode (flat list), not in
      // tree mode where indentation already conveys the hierarchy.
      if (!row.__searchResult || !treePath || treePath.length === 0)
        return base;
      const fullPath = treePath.join(' > ');
      return (
        <Box>
          {base}
          <Tooltip title={fullPath} placement="bottom-start" enterDelay={600}>
            <Box
              display="flex"
              alignItems="center"
              style={{ opacity: 0.6, overflow: 'hidden', minWidth: 0 }}
            >
              {treePath.map((segment, i) => (
                <Box
                  key={`${segment}-${i}`}
                  display="flex"
                  alignItems="center"
                  style={{
                    overflow: 'hidden',
                    minWidth: 0,
                    flexShrink: i === treePath.length - 1 ? 1 : 0,
                  }}
                >
                  {i > 0 && (
                    <NavigateNextIcon
                      style={{ fontSize: 14, color: 'inherit', flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {segment}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Tooltip>
        </Box>
      );
    };
  }

  return raw;
}

// ── Context ──────────────────────────────────────────────────────────────────

interface RdfConceptsContextType
  extends GenericProviderType<GenericEntityType[], ProviderFilters> {}

const RdfConceptsContext = createContext<RdfConceptsContextType | undefined>(
  undefined,
);

/**
 * Hook that reads the nearest RdfConceptsProvider context. Used by both
 * RdfConceptsHandlerProvider and BusinessConceptsHandlerProvider.
 */
export function useRdfConceptsContext(): RdfConceptsContextType {
  const ctx = useContext(RdfConceptsContext);
  if (!ctx) {
    throw new Error(
      'useRdfConceptsContext must be used inside RdfConceptsStateProvider',
    );
  }
  return ctx;
}

// ── State provider component ─────────────────────────────────────────────────

interface RdfConceptsStateProviderProps {
  kgmApi: KnowledgeGraphManagerApi;
  searchTreeParams: Omit<NodesResolveRequest, 'offset' | 'limit'>;
  labelProperty: string | undefined;
  templateKind: TemplateEntity;
  /**
   * When true, the initial tree view shows the first-level children of the
   * start nodes instead of the start nodes themselves. Defaults to false.
   */
  expandStartNodes?: boolean;
  /**
   * Number of children fetched per page when expanding or paginating a tree
   * node. Defaults to 20.
   */
  childrenPageSize?: number;
  /**
   * Alternative traversal + filtering used when expanding a node of a
   * specific `rdf:type` (see {@link SchemeExpansionConfig}). Optional.
   */
  schemeExpansion?: SchemeExpansionConfig;
  children: ReactNode;
}

/**
 * Manages pagination, search-filter state, and the async search-tree fetch.
 * Renders a fresh RdfConceptsContext.Provider around `children`.
 */
const RdfConceptsStateProvider: FC<RdfConceptsStateProviderProps> = ({
  kgmApi,
  searchTreeParams,
  labelProperty,
  templateKind,
  expandStartNodes,
  childrenPageSize,
  schemeExpansion,
  children,
}) => {
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_PAGE_SIZE,
    currentPage: 0,
  });
  const alertApi = useApi(customAlertApiRef);
  const [filters, setFilters] = useState<ProviderFilters>({});
  const [count, setCount] = useState<number>(0);
  // Number of children fetched per page when expanding/paginating tree nodes.
  const effectiveChildrenPageSize =
    childrenPageSize ?? DEFAULT_CHILDREN_PAGE_SIZE;
  // ── Tree mode state ────────────────────────────────────────────────────────
  const [treeChildren, setTreeChildren] = useState<
    Map<string, GenericEntityType[]>
  >(new Map());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [childrenPagination, setChildrenPagination] = useState<
    Map<
      string,
      { totalChildren: number; loadedChildren: number; hasNextPage: boolean }
    >
  >(new Map());

  const columns = useMemo(
    () => buildColumns(templateKind.columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateKind.columns],
  );

  // Serialise params to avoid re-running useAsync on every render for stable objects.
  const paramsKey = useMemo(
    () => JSON.stringify(searchTreeParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(searchTreeParams)],
  );

  const entitiesState = useAsync(async () => {
    const offset = pagination.currentPage * pagination.limit;

    // Search mode: use searchNodes to get matches with ancestor paths.
    // Only matches are displayed (fixing pagination); ancestors.paths + ancestors.details
    // are used to build __treePath so "Your selection" shows the breadcrumb path.
    if (filters.search && filters.search.trim() !== '') {
      // The default traverse only connects concepts to concepts (e.g.
      // skos:narrower/skos:broader). Search start nodes are typically
      // ConceptSchemes, which are only reachable to their concepts via
      // `schemeExpansion`'s relations (e.g. skos:inScheme). Without merging
      // those in, the backend BFS would never leave the start nodes and
      // search would always return zero matches.
      const { searchTraverse, ancestorTraverse } =
        buildSchemeAwareSearchTraverse(
          searchTreeParams.traverse,
          schemeExpansion,
        );

      const response = await kgmApi.searchNodes({
        ...searchTreeParams,
        traverse: searchTraverse,
        // Reconstruct ancestor paths preferring the original concept-to-concept
        // relations, only falling back to the widened `searchTraverse` (which
        // includes schemeExpansion's container relation) for nodes left
        // without a parent — otherwise a concept with both a real parent and
        // a container link (e.g. skos:inScheme) would get a non-deterministic
        // parent, sometimes skipping straight to the scheme in the breadcrumb.
        ancestorTraverse,
        searchText: filters.search,
        offset,
        limit: pagination.limit,
      });
      setCount(response.totalMatches);

      // Build a lookup: ancestor IRI → display label
      const ancestorLabelMap = new Map<string, string>();
      for (const detail of response.ancestors.details) {
        const label = resolveLabel(
          detail.id,
          detail.properties ?? {},
          labelProperty,
        );
        ancestorLabelMap.set(detail.id, label);
      }
      // Matches themselves may also appear as ancestors of other matches
      for (const match of response.matches) {
        const label = resolveLabel(
          match.id,
          match.properties ?? {},
          labelProperty,
        );
        ancestorLabelMap.set(match.id, label);
      }

      return response.matches.map(r => {
        const entity = toGenericEntityType(r, labelProperty);
        const ancestorIris = response.ancestors.paths[r.id] ?? [];
        if (ancestorIris.length > 0) {
          entity.__treePath = ancestorIris.map(
            iri => ancestorLabelMap.get(iri) ?? iri,
          );
        }
        entity.__searchResult = true;
        return entity;
      });
    }

    // Tree mode, expandStartNodes + schemeExpansion: the start nodes are
    // assumed to be `schemeExpansion.nodeType` (e.g. skos:ConceptScheme),
    // whose children are NOT reachable via the picker's default `traverse`
    // relations (e.g. skos:narrower/skos:broader) — only via
    // `schemeExpansion.traverse` (e.g. skos:inScheme). A depth=1 BFS using
    // the default traverse would therefore find zero children. Instead,
    // resolve the start (scheme) nodes at depth=0, then fetch each one's
    // children via `getChildren`, which already supports
    // `schemeExpansion.excludeDescendants` and computes the children's own
    // `childrenCount` using the default traverse relations (via
    // `childrenCountTraverse`), so they remain correctly expandable.
    if (expandStartNodes && schemeExpansion) {
      const schemesResponse = await kgmApi.resolveNodes({
        ...searchTreeParams,
        return: { ...searchTreeParams.return, includeStartNodes: true },
        traverse: { ...searchTreeParams.traverse, depth: 0 },
        offset: 0,
        limit: 100,
      });

      const childrenLists = await Promise.all(
        schemesResponse.nodes.map(scheme =>
          kgmApi.getChildren({
            parentId: scheme.id,
            traverse: schemeExpansion.traverse,
            excludeDescendants: schemeExpansion.excludeDescendants,
            childrenCountTraverse: {
              relations: searchTreeParams.traverse?.relations,
              inverseRelations: searchTreeParams.traverse?.inverseRelations,
            },
            return: searchTreeParams.return
              ? { properties: searchTreeParams.return.properties }
              : undefined,
            prefixMap: searchTreeParams.prefixMap,
            offset: 0,
            limit: 100_000, // fetch all; paginated in-memory below
          }),
        ),
      );

      const allChildren = childrenLists.flatMap(r => r.nodes);
      setCount(allChildren.length);
      const page = allChildren.slice(offset, offset + pagination.limit);
      return page.map((r: NodeResult) => toGenericEntityType(r, labelProperty));
    }

    // Tree mode: fetch only root nodes (seed nodes, depth=0), unless
    // `expandStartNodes` is set, in which case fetch the first-level children
    // of the start nodes instead (depth=1, seed nodes excluded).
    const response = await kgmApi.resolveNodes({
      ...searchTreeParams,
      return: {
        ...searchTreeParams.return,
        includeStartNodes: !expandStartNodes,
      },
      traverse: {
        ...searchTreeParams.traverse,
        depth: expandStartNodes ? 1 : 0,
      },
      offset,
      limit: pagination.limit,
      // Root nodes matching schemeExpansion.nodeType (e.g. a skos:ConceptScheme)
      // expose their children via schemeExpansion's relations, not the
      // default traverse relations — without this override their reported
      // childrenCount would be 0 and they'd render as non-expandable leaves.
      childrenCountOverride: schemeExpansion
        ? {
            nodeType: schemeExpansion.nodeType,
            relations: schemeExpansion.traverse?.relations,
            inverseRelations: schemeExpansion.traverse?.inverseRelations,
          }
        : undefined,
    });
    setCount(response.total);
    return response.nodes.map((r: NodeResult) =>
      toGenericEntityType(r, labelProperty),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kgmApi,
    paramsKey,
    labelProperty,
    filters.search,
    pagination.limit,
    pagination.currentPage,
    expandStartNodes,
    schemeExpansion,
  ]);

  // ── Tree mode callbacks ────────────────────────────────────────────────────

  // Lookup of node `rdf:type` by IRI, built from every node observed so far
  // (root-level results + already-loaded children), used to decide whether
  // `schemeExpansion` applies when expanding a given node. Nodes are looked
  // up by plain IRI (not __treeId) since type is intrinsic to the node, not
  // to the tree position it's displayed at.
  const nodeTypeByIri = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of entitiesState.value ?? []) {
      map.set(node.id as string, node.type as string);
    }
    for (const childList of treeChildren.values()) {
      for (const child of childList) {
        map.set(child.id as string, child.type as string);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitiesState.value, treeChildren]);

  /**
   * Resolves the traverse config + excludeDescendants to use when fetching
   * the children of `iri`: `schemeExpansion`'s when the node's `rdf:type`
   * matches `schemeExpansion.nodeType`, the picker's default `traverse`
   * otherwise.
   */
  const resolveChildrenTraversal = useCallback(
    (
      iri: string,
    ): {
      traverse:
        | Omit<NonNullable<NodesResolveRequest['traverse']>, 'depth'>
        | undefined;
      excludeDescendants?: ExcludeDescendants;
      /**
       * When the node being expanded uses `schemeExpansion`'s traversal
       * (e.g. `skos:inScheme`) to reach its children, those children's own
       * `childrenCount` must still be computed using the picker's default
       * traverse relations (e.g. `skos:narrower`/`skos:broader`), not the
       * scheme-specific ones — otherwise they'd be wrongly reported as
       * childless and rendered as non-expandable leaves.
       */
      childrenCountTraverse?: {
        relations?: string[];
        inverseRelations?: string[];
      };
    } => {
      const nodeType = nodeTypeByIri.get(iri);
      if (schemeExpansion && nodeType === schemeExpansion.nodeType) {
        return {
          traverse: schemeExpansion.traverse ?? searchTreeParams.traverse,
          excludeDescendants: schemeExpansion.excludeDescendants,
          childrenCountTraverse: {
            relations: searchTreeParams.traverse?.relations,
            inverseRelations: searchTreeParams.traverse?.inverseRelations,
          },
        };
      }
      return { traverse: searchTreeParams.traverse };
    },
    [nodeTypeByIri, schemeExpansion, searchTreeParams.traverse],
  );

  const onExpand = useCallback(
    async (nodeId: string) => {
      // nodeId may be a __treeId (parentIRI::childIRI); extract the raw IRI.
      const iri = nodeId.includes('::') ? nodeId.split('::').pop()! : nodeId;
      if (treeChildren.has(nodeId) || loadingNodes.has(nodeId)) return;
      setLoadingNodes(prev => new Set([...prev, nodeId]));
      try {
        const {
          traverse: effectiveTraverse,
          excludeDescendants,
          childrenCountTraverse,
        } = resolveChildrenTraversal(iri);
        const response = await kgmApi.getChildren({
          parentId: iri,
          traverse: effectiveTraverse
            ? {
                relations: effectiveTraverse.relations,
                relationsNot: effectiveTraverse.relationsNot,
                inverseRelations: effectiveTraverse.inverseRelations,
                inverseRelationsNot: effectiveTraverse.inverseRelationsNot,
                nodeTypes: effectiveTraverse.nodeTypes,
                nodeTypesNot: effectiveTraverse.nodeTypesNot,
              }
            : undefined,
          excludeDescendants,
          childrenCountTraverse,
          return: searchTreeParams.return
            ? { properties: searchTreeParams.return.properties }
            : undefined,
          prefixMap: searchTreeParams.prefixMap,
          offset: 0,
          limit: effectiveChildrenPageSize,
        });
        const loadedChildren = response.nodes.map((r: NodeResult) =>
          toGenericEntityType(r, labelProperty),
        );
        setTreeChildren(prev => new Map([...prev, [nodeId, loadedChildren]]));
        setChildrenPagination(
          prev =>
            new Map([
              ...prev,
              [
                nodeId,
                {
                  totalChildren: response.total,
                  loadedChildren: response.nodes.length,
                  hasNextPage: response.hasNextPage,
                },
              ],
            ]),
        );
      } catch (error) {
        alertApi.post({ error, severity: 'error' });
      } finally {
        setLoadingNodes(prev => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      treeChildren,
      loadingNodes,
      kgmApi,
      paramsKey,
      labelProperty,
      effectiveChildrenPageSize,
      alertApi,
      resolveChildrenTraversal,
    ],
  );

  const onLoadMoreChildren = useCallback(
    async (nodeId: string) => {
      // nodeId may be a __treeId (parentIRI::childIRI); extract the raw IRI.
      const iri = nodeId.includes('::') ? nodeId.split('::').pop()! : nodeId;
      const pag = childrenPagination.get(nodeId);
      if (!pag || !pag.hasNextPage || loadingNodes.has(nodeId)) return;
      setLoadingNodes(prev => new Set([...prev, nodeId]));
      try {
        const {
          traverse: effectiveTraverse,
          excludeDescendants,
          childrenCountTraverse,
        } = resolveChildrenTraversal(iri);
        const response = await kgmApi.getChildren({
          parentId: iri,
          traverse: effectiveTraverse
            ? {
                relations: effectiveTraverse.relations,
                relationsNot: effectiveTraverse.relationsNot,
                inverseRelations: effectiveTraverse.inverseRelations,
                inverseRelationsNot: effectiveTraverse.inverseRelationsNot,
              }
            : undefined,
          excludeDescendants,
          childrenCountTraverse,
          return: searchTreeParams.return
            ? { properties: searchTreeParams.return.properties }
            : undefined,
          prefixMap: searchTreeParams.prefixMap,
          offset: pag.loadedChildren,
          limit: effectiveChildrenPageSize,
        });
        const newChildren = response.nodes.map((r: NodeResult) =>
          toGenericEntityType(r, labelProperty),
        );
        setTreeChildren(prev => {
          const existing = prev.get(nodeId) ?? [];
          return new Map([...prev, [nodeId, [...existing, ...newChildren]]]);
        });
        setChildrenPagination(
          prev =>
            new Map([
              ...prev,
              [
                nodeId,
                {
                  totalChildren: response.total,
                  loadedChildren: pag.loadedChildren + response.nodes.length,
                  hasNextPage: response.hasNextPage,
                },
              ],
            ]),
        );
      } catch (error) {
        alertApi.post({ error, severity: 'error' });
      } finally {
        setLoadingNodes(prev => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      childrenPagination,
      loadingNodes,
      kgmApi,
      paramsKey,
      labelProperty,
      effectiveChildrenPageSize,
      alertApi,
      resolveChildrenTraversal,
    ],
  );

  const getRowChildren = useCallback(
    (row: GenericEntityType): GenericEntityType[] => {
      // Sentinel rows have no children of their own
      if (row.__loadingParentId || row.__loadMoreParentId) return [];

      // Use __treeId when available (children under a specific parent), fall
      // back to id for root-level rows.
      const treeId = (row.__treeId as string | undefined) ?? (row.id as string);
      const loaded = treeChildren.get(treeId) ?? [];
      const pag = childrenPagination.get(treeId);
      const isLoading = loadingNodes.has(treeId);
      const childrenCount = row.childrenCount as number | undefined;

      // If not yet expanded and childrenCount is known to be 0, skip
      if (loaded.length === 0 && !isLoading && childrenCount === 0) return [];

      // Build the ancestry path for children: parent's own path + parent's label.
      // __treePath is an array of ancestor display labels used in "Your selection".
      const parentPath = (row.__treePath as string[] | undefined) ?? [];
      const parentLabel = row.__metadata?.name as string;
      const childPath = [...parentPath, parentLabel];

      // Annotate children with their ancestry path and a unique tree identity.
      // __treeId encodes the parent→child relationship so that the same concept
      // appearing under different parents gets distinct row IDs in WbTable.
      const result: GenericEntityType[] = loaded.map(child => ({
        ...child,
        __treePath: childPath,
        __treeId: `${treeId}::${child.id as string}`,
      }));

      // Loading sentinel when loading and no children visible yet
      if (isLoading && loaded.length === 0) {
        return [
          {
            __metadata: { name: '__loading__', kind: KindEnum.businessConcept },
            id: `__loading__${treeId}`,
            type: '',
            properties: {},
            childrenCount: 0,
            __loadingParentId: treeId,
          } as GenericEntityType,
        ];
      }

      // Loading sentinel at end when paginating children
      if (isLoading) {
        result.push({
          __metadata: { name: '__loading__', kind: KindEnum.businessConcept },
          id: `__loading__${treeId}`,
          type: '',
          properties: {},
          childrenCount: 0,
          __loadingParentId: treeId,
        } as GenericEntityType);
      }

      // Load-more sentinel when more pages exist
      if (pag?.hasNextPage && !isLoading) {
        result.push({
          __metadata: { name: '__loadMore__', kind: KindEnum.businessConcept },
          id: `__loadMore__${treeId}`,
          type: '',
          properties: {},
          childrenCount: 0,
          __loadMoreParentId: treeId,
        } as GenericEntityType);
      }

      return result;
    },
    [treeChildren, childrenPagination, loadingNodes],
  );

  const getEntityId = useCallback(
    (row: GenericEntityType): string =>
      (row.__treeId as string | undefined) ?? (row.id as string),
    [],
  );

  // Disable tree mode when search is active (show flat results instead)
  const effectiveTreeMode = filters.search
    ? undefined
    : {
        getRowChildren,
        getEntityId,
        onExpand,
        onLoadMoreChildren,
        loadingRowsIds: Array.from(loadingNodes),
        rowChildrenPagination: childrenPagination,
        hasChildren: (row: GenericEntityType) => {
          if ((row as any).__loadingParentId || (row as any).__loadMoreParentId)
            return false;
          const tid =
            (row.__treeId as string | undefined) ?? (row.id as string);
          const loaded = treeChildren.get(tid) ?? [];
          if (loaded.length > 0) return true;
          return ((row.childrenCount as number | undefined) ?? 0) > 0;
        },
      };

  const changeFilters = useCallback(
    <K extends keyof ProviderFilters>(key: K, value: ProviderFilters[K]) => {
      setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
      setFilters(f => ({ ...f, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => setFilters({}), []);

  return (
    <RdfConceptsContext.Provider
      value={{
        entitiesState,
        columns,
        filters,
        changeFilters,
        resetFilters,
        templateKind,
        pagination,
        setPagination,
        count,
        treeMode: effectiveTreeMode,
      }}
    >
      {children}
    </RdfConceptsContext.Provider>
  );
};

// ── Filters section ──────────────────────────────────────────────────────────

const RdfConceptsFiltersSection = () => {
  const { filters, changeFilters, resetFilters, templateKind } =
    useRdfConceptsContext();

  return (
    <WbResponsiveTableToolbar onClear={resetFilters} marginBottom>
      <WbResponsiveTableToolbar.Filters>
        {templateKind?.userFilters?.map(key => {
          if (key === 'search') {
            return (
              <SearchFilter
                key={key}
                value={filters.search ?? ''}
                changeFilters={changeFilters}
              />
            );
          }
          return null;
        })}
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

// ── Handler class ────────────────────────────────────────────────────────────

export interface RdfConceptsHandlerProviderOptions {
  kgmApi: KnowledgeGraphManagerApi;
  /** Stable node-browse params derived from the provider configuration. */
  searchTreeParams: Omit<NodesResolveRequest, 'offset' | 'limit'>;
  /**
   * Property (IRI or prefixed name) whose value is used as the display label.
   * Falls back to the node URI when not set.
   */
  labelProperty?: string;
  /**
   * When true, the initial tree view shows the first-level children of the
   * start nodes instead of the start nodes themselves. Defaults to false.
   */
  expandStartNodes?: boolean;
  /**
   * Number of children fetched per page when expanding or paginating a tree
   * node. Defaults to 20.
   */
  childrenPageSize?: number;
  /**
   * Alternative traversal + filtering used when expanding a node of a
   * specific `rdf:type` (see {@link SchemeExpansionConfig}). Optional.
   */
  schemeExpansion?: SchemeExpansionConfig;
}

/**
 * Generic RDF knowledge-graph provider implementing the EntitySearchPicker
 * `Provider` interface. Calls the `/search-tree` API and presents results in
 * `WbGeneralPurposeEntityPicker`.
 *
 * This is an internal base used by higher-level providers such as
 * `BusinessConceptsHandlerProvider`. It is not registered directly in
 * `AvailableEntities`.
 */
export class RdfConceptsHandlerProvider
  implements Provider<GenericEntityType[], ProviderFilters>
{
  protected readonly kgmApi: KnowledgeGraphManagerApi;
  protected readonly searchTreeParams: Omit<
    NodesResolveRequest,
    'offset' | 'limit'
  >;
  protected readonly labelProperty: string | undefined;
  protected readonly expandStartNodes: boolean | undefined;
  protected readonly childrenPageSize: number | undefined;
  protected readonly schemeExpansion: SchemeExpansionConfig | undefined;
  /** IRI → ordered label segments from root to leaf (inclusive). */
  private readonly treePathCache = new Map<string, string[]>();

  constructor({
    kgmApi,
    searchTreeParams,
    labelProperty,
    expandStartNodes,
    childrenPageSize,
    schemeExpansion,
  }: RdfConceptsHandlerProviderOptions) {
    this.kgmApi = kgmApi;
    this.searchTreeParams = searchTreeParams;
    this.labelProperty = labelProperty;
    this.expandStartNodes = expandStartNodes;
    this.childrenPageSize = childrenPageSize;
    this.schemeExpansion = schemeExpansion;
  }

  // Assign the module-level hook so callers get the correct React context.
  useContext = useRdfConceptsContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
  ): JSX.Element {
    const effectiveKind: TemplateEntity = templateKind ?? {
      type: 'RdfConcept',
      displayName: 'RDF Concept',
      userFilters: ['search'],
    };
    return (
      <RdfConceptsStateProvider
        kgmApi={this.kgmApi}
        searchTreeParams={this.searchTreeParams}
        labelProperty={this.labelProperty}
        templateKind={effectiveKind}
        expandStartNodes={this.expandStartNodes}
        childrenPageSize={this.childrenPageSize}
        schemeExpansion={this.schemeExpansion}
      >
        {children}
      </RdfConceptsStateProvider>
    );
  }

  filtersSection = RdfConceptsFiltersSection;

  adapt(entity: GenericEntityType): AdaptedEntity {
    const iri = entity.id as string;
    // __metadata.name is set by toGenericEntityType to the resolved label.
    const leafLabel = (entity.__metadata?.name as string | undefined) ?? iri;
    // When the entity was loaded as a tree child, __treePath contains the
    // ancestor labels from root to the node's parent.
    const treePath = entity.__treePath as string[] | undefined;
    const fullPath =
      treePath && treePath.length > 0 ? [...treePath, leafLabel] : undefined;
    // Cache so resolve() can restore treePath without extra API calls.
    if (fullPath) {
      this.treePathCache.set(iri, fullPath);
    }
    return {
      name: leafLabel,
      kind: KindEnum.businessConcept,
      label: leafLabel,
      ref: iri,
      // RDF concepts have no Witboost URN — return the IRI as fallback.
      urn: iri,
      treePath: fullPath,
    };
  }

  async resolve(ref: string): Promise<TextFieldValue | undefined> {
    // Fast path: return cached treePath so the chip hierarchy is restored
    // immediately when the user navigates away and comes back.
    const cached = this.treePathCache.get(ref);
    if (cached) {
      const label = cached[cached.length - 1];
      return {
        kind: KindEnum.businessConcept,
        label,
        value: ref,
        treePath: cached,
      };
    }
    try {
      // Fetch ancestor path so the resolved entity carries full treePath.
      // This populates the cache for future calls and avoids a separate
      // lazy-load round-trip when the user clicks the chip.
      const treePath = await this.fetchAncestors(ref);
      if (treePath && treePath.length > 0) {
        const label = treePath[treePath.length - 1];
        return { kind: KindEnum.businessConcept, label, value: ref, treePath };
      }
      // fetchAncestors failed or returned empty — fall back to label-only resolve.
      // depth: 0 and includeStartNodes: true ensure we get back the exact
      // node matching the IRI, not an arbitrary descendant from a BFS traversal.
      const response = await this.kgmApi.resolveNodes({
        ...this.searchTreeParams,
        startNode: [[{ predicate: '@id', in: [ref] }]],
        traverse: { ...this.searchTreeParams.traverse, depth: 0 },
        return: { ...this.searchTreeParams.return, includeStartNodes: true },
        offset: 0,
        limit: 1,
      });
      const result = response.nodes[0];
      if (result) {
        const label = resolveLabel(
          result.id,
          result.properties ?? {},
          this.labelProperty,
        );
        return { kind: KindEnum.businessConcept, label, value: ref };
      }
    } catch {
      // Fall through to IRI fallback so the chip shows even if the API call fails.
    }
    return { kind: KindEnum.businessConcept, label: ref, value: ref };
  }

  extractEntityId(entity: GenericEntityType): string | undefined {
    return entity.id as string | undefined;
  }

  async fetchAncestors(ref: string): Promise<string[] | undefined> {
    try {
      // First resolve the node label so we can use it as searchText.
      // depth: 0 and includeStartNodes: true ensure we get back the exact
      // node matching the IRI, not an arbitrary descendant reached via BFS
      // traversal (searchTreeParams.traverse has no depth limit by default,
      // which would otherwise let getNodes return any node within 100 hops).
      const nodeResponse = await this.kgmApi.resolveNodes({
        ...this.searchTreeParams,
        startNode: [[{ predicate: '@id', in: [ref] }]],
        traverse: { ...this.searchTreeParams.traverse, depth: 0 },
        return: { ...this.searchTreeParams.return, includeStartNodes: true },
        offset: 0,
        limit: 1,
      });
      const node = nodeResponse.nodes[0];
      if (!node) return undefined;

      const leafLabel = resolveLabel(
        node.id,
        node.properties ?? {},
        this.labelProperty,
      );

      // Now search from the ORIGINAL startNode (concept scheme root) so the
      // API can compute the full ancestor path from root down to this node.
      // Widen the traverse with `schemeExpansion`'s relations (e.g.
      // skos:inScheme) — otherwise the BFS can never leave the start nodes
      // for vocabularies whose top concepts are only reachable that way,
      // and the match lookup below would silently fail, leaving the entity
      // without any ancestors (see buildSchemeAwareSearchTraverse).
      const { searchTraverse, ancestorTraverse } =
        buildSchemeAwareSearchTraverse(
          this.searchTreeParams.traverse,
          this.schemeExpansion,
        );
      const response = await this.kgmApi.searchNodes({
        ...this.searchTreeParams,
        traverse: searchTraverse,
        ancestorTraverse,
        searchText: leafLabel,
        offset: 0,
        limit: 50,
      });

      // Find the exact match by IRI among the (possibly many) text matches.
      const match = response.matches.find(m => m.id === node.id);
      if (!match) return [leafLabel];

      // Build ancestor label lookup
      const ancestorLabelMap = new Map<string, string>();
      for (const detail of response.ancestors.details) {
        ancestorLabelMap.set(
          detail.id,
          resolveLabel(detail.id, detail.properties ?? {}, this.labelProperty),
        );
      }

      const ancestorIris = response.ancestors.paths[match.id] ?? [];
      const ancestorLabels = ancestorIris.map(
        iri => ancestorLabelMap.get(iri) ?? iri,
      );

      const result = [...ancestorLabels, leafLabel];
      // Populate cache so subsequent resolve() calls are instant.
      this.treePathCache.set(ref, result);
      return result;
    } catch {
      return undefined;
    }
  }
}
